import { DeleteIcon } from "@chakra-ui/icons";
import { Box, Grid } from "@chakra-ui/react";
import { Alert, AlertDescription, AlertIcon, AlertTitle, Text } from "@chakra-ui/react";
import { LineString } from "geojson";
import { FC, useCallback, useState } from "react";

import { RevokeIcon } from "@nens/ranalib";
import { ThreediModel } from "@nens/threedi/openapi-client";

import { getThreediModelsApi } from "../../apiConfiguration";
import AlertBox from "../../components/AlertBox";
import ButtonWithConfirmation from "../../components/ButtonWithConfirmation";
import GenerateAndOpenLink from "../../components/GenerateAndOpenLink";
import { GridCard } from "../../components/GridCard";
import InternalLink from "../../components/InternalLink";
import { useThreediVersion } from "../../queries/general";
import { deleteThreediModel, recreateThreediModel } from "../../queries/threedimodels";
import { useGridadmin } from "../../queries/threedimodels";
import { combineExtents, extentToLeaflet } from "../../utils/geo";
import ExtentMap from "../models/ExtentMap";

import ModelSimulationTemplatesCard from "./ModelSimulationTemplatesCard";

const threediModelsApi = getThreediModelsApi();

interface ModelDetailsProps {
  callbackWhenModelDeleted: () => void;
  model: ThreediModel;
}

const ModelDetails: FC<ModelDetailsProps> = ({ callbackWhenModelDeleted, model }) => {
  const threediVersion = useThreediVersion();

  const [deleteModelErrorMessages, setDeleteModelErrorMessages] = useState<string[]>([]);

  const [regenerateModelErrorMessages, setRegenerateModelErrorMessages] = useState<string[]>([]);
  const reCreateModel = useCallback(
    () =>
      recreateThreediModel(model, (messages) => {
        setRegenerateModelErrorMessages(messages);
        callbackWhenModelDeleted();
      }),
    [model, callbackWhenModelDeleted]
  );

  const deleteModel = useCallback(
    () =>
      deleteThreediModel(model, (messages) => {
        setDeleteModelErrorMessages(messages);
        callbackWhenModelDeleted();
      }),
    [model, callbackWhenModelDeleted]
  );

  const gridadminResponse = useGridadmin(model ? model.id : undefined);
  const gridadmin = gridadminResponse.isSuccess ? gridadminResponse.data : null;

  return (
    <Grid gap="1rem" templateColumns="repeat(4, 1fr)">
      <GridCard colSpan={2} title="Rana Model info">
        <Box>Projection: EPSG:{model.epsg}</Box>
        <Box>Storage: {model.storageSpaceHumanized}</Box>
        <Box>Node count: {model.nodesCount}</Box>
        <Box>Line count: {model.linesCount}</Box>
        <Box>Breach count: {model.breachCount}</Box>
        {gridadmin?.getUrl ? (
          <Box display={"flex"}>
            <Text marginRight="0.5rem">Gridadmin file:</Text>
            <GenerateAndOpenLink
              filename={"gridadmin.h5"}
              size={gridadmin.size || 0}
              fetchUrl={async () => {
                return (
                  await threediModelsApi.threedimodelsGridadminDownload({
                    id: model.id ?? 0,
                  })
                ).getUrl;
              }}
            />
          </Box>
        ) : null}
      </GridCard>
      <GridCard colSpan={2} title="Re-generate">
        <p>
          This model was generated using HCC framework version {model.threediVersion}.
          {threediVersion !== null ? ` The current version is ${threediVersion}.` : null}
        </p>
        {threediVersion !== null ? (
          <p>
            The model is {threediVersion === model.threediVersion ? "" : <strong>not </strong>} up
            to date.
          </p>
        ) : null}
        <ButtonWithConfirmation
          buttonText="Re-generate this Rana model"
          dialogTitle="Really re-generate?"
          confirmationButtonText="Re-generate"
          confirmationButtonSafety="neutral"
          onClick={reCreateModel}
          marginRight="0.5rem"
          marginY="0.25rem"
          variant="danger"
          buttonIcon={<RevokeIcon boxSize={5} />}
          disableAfterClick
          needsCreatorRole
        >
          <p>
            This deletes this revision's current Rana model, and runs the model generation process
            again.
          </p>
          {threediVersion === model.threediVersion ? (
            <p>
              Note: this model was created by the current HCC framework version ({threediVersion}),
              so it is fully up to date. Re-generating it should have no effect.
            </p>
          ) : null}
          <p>Re-generate this model?</p>
        </ButtonWithConfirmation>
        <>
          {regenerateModelErrorMessages.map((errorMsg) => {
            if (
              errorMsg ===
              "This threedimodel is being used in active simulations, please end these before deleting."
            ) {
              return (
                <Alert status="error">
                  <AlertIcon />
                  <AlertTitle>Regeneration failed!</AlertTitle>
                  <AlertDescription>
                    <div>{errorMsg}</div>
                    <InternalLink to={`/live`} target="blank">
                      {"Find active simulations"}
                    </InternalLink>
                  </AlertDescription>
                </Alert>
              );
            }
            return (
              <Alert status="error">
                <AlertIcon />
                <AlertTitle>Regeneration failed!</AlertTitle>
                <AlertDescription>{errorMsg}</AlertDescription>
              </Alert>
            );
          })}
        </>
        <ButtonWithConfirmation
          buttonText="Delete this Rana model"
          dialogTitle="Really delete?"
          confirmationButtonText="Delete"
          confirmationButtonSafety="dangerous"
          buttonIcon={<DeleteIcon boxSize={5} />}
          onClick={deleteModel}
          disableAfterClick
          marginY="0.25rem"
          needsCreatorRole
        >
          <p>
            This deletes this Rana model, so that no new simulations can be run with it. This can be
            used to free up storage space. The model can later be generated again.
          </p>
        </ButtonWithConfirmation>
        <>
          {deleteModelErrorMessages.map((errorMsg) => {
            if (
              errorMsg ===
              "This threedimodel is being used in active simulations, please end these before deleting."
            ) {
              return (
                <AlertBox status={"error"} title={"Deletion failed!"}>
                  <div>{errorMsg}</div>
                  <InternalLink to={`/live`} target="blank">
                    {"Find active simulations"}
                  </InternalLink>
                </AlertBox>
              );
            }
            return (
              <AlertBox status={"error"} title={"Deletion failed!"}>
                {errorMsg}
              </AlertBox>
            );
          })}
        </>
      </GridCard>
      <GridCard colSpan={2} title="Model extent">
        <Box height="16rem">
          <ExtentMap
            extent={combineExtents([
              extentToLeaflet(model.extentZeroD as LineString | null | undefined),
              extentToLeaflet(model.extentOneD as LineString | null | undefined),
              extentToLeaflet(model.extentTwoD as LineString | null | undefined),
            ])}
            borderWidth="thin"
            borderStyle="solid"
            borderColor="brand.glacier"
          />
        </Box>
      </GridCard>
      {/* Sadly enough this card must not be on the left side of the
          map card, because the action menu of the table will go
          *under* the map. So we put it last. */}
      <ModelSimulationTemplatesCard threediModel={model} />
    </Grid>
  );
};

export default ModelDetails;
